import {getFirebaseEnvironment} from "@buildwithflux/constants";
import {set} from "lodash";

export function hasBrowserWindowAccess(): boolean {
    try {
        return typeof window !== "undefined";
    } catch (err) {
        return false;
    }
}

export function hasDocumentAccess(): boolean {
    try {
        return typeof document !== "undefined";
    } catch (err) {
        return false;
    }
}

export function hasNavigatorAccess(): boolean {
    try {
        return typeof navigator !== "undefined";
    } catch (err) {
        return false;
    }
}

export function hasSelfAccess(): boolean {
    try {
        // eslint-disable-next-line no-restricted-globals
        return typeof self !== "undefined";
    } catch (err) {
        return false;
    }
}

/**
 * Whether the top level 'process' variable is accessible
 *
 * Note that this returns false in the normal web context: process is a node-specific global. However, the bundling
 * makes it appear as if specific accesses to process.env.SOME_ENV_VAR are succeeding: in reality, these are being
 * rewritten by the bundler to refer to a static value.
 */
export function hasProcessAccess(): boolean {
    try {
        return typeof process !== "undefined";
    } catch (err) {
        return false;
    }
}

export function hasGlobalAccess(): boolean {
    try {
        return typeof global !== "undefined";
    } catch (err) {
        return false;
    }
}

export function isNode(): boolean {
    try {
        return !hasGlobalAccess() && "[object global]" === Object.prototype.toString.call(global);
    } catch (err) {
        return false;
    }
}

export function isWebWorker(): boolean {
    try {
        // On a page `self` is the window, so only the worker global scope tells a worker apart
        const workerGlobalScope = (globalThis as {WorkerGlobalScope?: abstract new () => unknown}).WorkerGlobalScope;
        // eslint-disable-next-line no-restricted-globals -- self
        return !isNode() && workerGlobalScope !== undefined && hasSelfAccess() && self instanceof workerGlobalScope;
    } catch (err) {
        return false;
    }
}

export function isBrowser(): boolean {
    return !isNode() && !isWebWorker() && hasNavigatorAccess() && hasDocumentAccess();
}

/**
 * Please note this will only return true if a user is in local environment.
 * If running in a test environment, this will NOT be true!
 *
 * @returns true if this is running in local environment (i.e. localhost)
 */
export function isDevEnv(): boolean {
    if (process.env.NODE_ENV !== "development") {
        return false;
    }

    if (hasBrowserWindowAccess()) {
        // NOTE: window.navigator.userAgent is set to "fluxsandbox" in VisualTestHelper.ts -> `getVisualTestHelper`
        return window.navigator.userAgent !== "fluxsandbox";
    }

    return true;
}

export function areWeTestingWithJest(): boolean {
    return hasProcessAccess() && process?.env?.JEST_WORKER_ID !== undefined;
}

export function areWeInStorybook(): boolean {
    return hasProcessAccess() && !!process.env.STORYBOOK;
}

/**
 * Whether this build should use local services for some backends
 *
 * Note we don't check hasProcessAccess() here, because these environment variables are provided by defines in the
 * frontend bundling system (inherited from CRA)
 */
export function shouldUseLocalServices(specificService: "foo" | undefined = undefined): boolean {
    if (!isDevEnv()) {
        return false;
    }

    // NOTE: don't try to refactor this to use a dynamic env var name, that doesn't work with DefinePlugin
    return (
        getFirebaseEnvironment() === "emulated" ||
        (specificService === "foo" && process.env.REACT_APP_USE_LOCAL_FOO === "true")
    );
}

export function isCypressTestUserAgent(): boolean {
    return hasBrowserWindowAccess() && !!window?.navigator?.userAgent?.match(/cypress/i);
}

/**
 * Are we testing?
 */
export function isTestEnv(): boolean {
    return areWeTestingWithJest() || isCypressTestUserAgent();
}

/**
 * Whether the current browser environment supports Web Workers.
 *
 * NOTE: we intentionally do NOT test `typeof window.onmessage === "object"`.
 * `window.onmessage` is an event-handler IDL attribute that is `null` by
 * default (`typeof null === "object"`), but it legitimately becomes a function
 * the moment any code assigns a window-level `message` handler. That makes it
 * an invalid signal for worker support: a single unrelated `window.onmessage =`
 * assignment anywhere on the page would flip this check to `false` even though
 * Web Workers are fully available. The presence of the `Worker` constructor
 * (plus `postMessage`) is the correct capability probe.
 */
export function areWebWorkersSupported(): boolean {
    return hasBrowserWindowAccess() && typeof window.Worker !== "undefined" && typeof window.postMessage === "function";
}

/**
 * Names of supported environments.
 */
export const EnvironmentName = {
    // The big 3
    Production: "production",
    Release: "release",
    Main: "main",

    Preview: "preview", // deploy preview
    Branch: "branch", // branch deploy
    Development: "development", // A default deploy that doesn't set the value (NOT localhost:3000)

    Local: "local", // localhost:3000, pnpm start
} as const;
export type EnvironmentName = (typeof EnvironmentName)[keyof typeof EnvironmentName];

/**
 * Environment values stamped into the served index.html by the app proxy at
 * request time (derived from the request hostname and per-host deploy state —
 * never baked into the artifact). The frontend bundle is byte-identical across
 * all environments, so anything environment-specific must come from here or
 * from the hostname itself, synchronously (no fetches).
 */
export interface FluxStampedEnv {
    environmentName?: string;
    mergeRequestId?: string;
    appEnv?: string;
    domainPrefix?: string;
    appVersion?: string;
    appVersionExtended?: string;
    commitSha?: string;
    commitDate?: string;
    commitTimestamp?: string;
    /**
     * Analytics opt-out for the serving deploy. Absence means analytics are
     * enabled; deploys that opt out (e.g. PR previews without the
     * analytics-enabled label) are stamped with true.
     */
    analyticsDisabled?: boolean;
    /**
     * sha256 of the exact deployed artifact tree — the content address the
     * serving bundle is addressed by in R2.
     */
    artifactHash?: string;
    /**
     * Build/source-input identity (turbo build hash) of the serving bundle;
     * provenance only, never used for addressing.
     */
    buildInputHash?: string;
    flagArtifactSeed?: FlagArtifactSeed;
}

export interface FlagArtifactSeed {
    ordinal: number;
    contentHash: string;
    streamId: string;
    streamGeneration?: number;
    activationSequence?: number;
    programHash?: string;
    programFormat?: string;
    programVersion?: number;
    programMode?: string;
}

declare global {
    interface Window {
        __FLUX_ENV__?: FluxStampedEnv;
    }
}

/**
 * Reads the proxy-stamped environment block, if present. Synchronous, never
 * fetches. Absent on localhost, in tests, and behind proxies that predate
 * stamping — callers must handle the hostname-derived fallback.
 */
export function getStampedEnv(): FluxStampedEnv | undefined {
    return hasBrowserWindowAccess() ? window.__FLUX_ENV__ : undefined;
}

/**
 * Derives the environment name from a hostname. The single fallback used when
 * the proxy stamp is absent. Unknown *.flux.ai hosts are treated as production
 * (they serve the production artifact); anything else is local.
 */
export function getEnvironmentNameFromHostname(hostname: string): EnvironmentName | undefined {
    if (/^pr-\d+\.(preview|internal)\.flux\.ai$/.test(hostname)) {
        return EnvironmentName.Preview;
    }
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local")) {
        return EnvironmentName.Local;
    }
    if (hostname === "flux.ai" || hostname.endsWith(".flux.ai")) {
        return EnvironmentName.Production;
    }
    return undefined;
}

/**
 * The PR number of the current merge-request preview environment, or
 * undefined outside previews. Stamped value first, hostname fallback.
 */
export function getMergeRequestId(): string | undefined {
    const stamped = getStampedEnv()?.mergeRequestId;
    if (stamped) {
        return stamped;
    }
    if (hasBrowserWindowAccess()) {
        const match = window.location.hostname.match(/^pr-(\d+)\.(preview|internal)\.flux\.ai$/);
        if (match) {
            return match[1];
        }
    }
    return undefined;
}

/**
 * Please note this will only return true if a user is in prod.
 * If running in a test environment, this will NOT be true!
 * Sometimes we might want to allow operations in both prod and RF tests, then
 * this is NOT the function you want to use! For example, see the
 * `process.env.NODE_ENV === "production"` in `serviceWorkerRegistration.ts`
 *
 * @returns true if this is running in production environment (e.g. frontend on www.flux.ai, without a split-testing override, or backend on our main Firebase account)
 */
export function isProductionEnvironment(): boolean {
    return getEnvironmentName() === EnvironmentName.Production;
}

/**
 * Gets the current environment name, derived at RUNTIME — never from anything
 * baked into a bundle. The frontend artifact is byte-identical across all
 * environments; environment identity comes from (in order):
 *   1. the proxy-stamped window.__FLUX_ENV__ block (request-time, per host)
 *   2. the page hostname
 *   3. the Node process environment (backends only)
 */
export function getEnvironmentName(): EnvironmentName | undefined {
    const stamped = getStampedEnv()?.environmentName;
    if (stamped) {
        return stamped as EnvironmentName;
    }
    if (hasBrowserWindowAccess() && !areWeTestingWithJest()) {
        return getEnvironmentNameFromHostname(window.location.hostname);
    }
    return getEnvironmentNameFromNodeProcessEnv();
}

/**
 * Returns the environment name from the Node process environment (backends,
 * tests). In the browser bundle REACT_APP_ENVIRONMENT_NAME is deliberately
 * NOT defined — one canonical artifact serves every environment.
 */
export function getEnvironmentNameFromNodeProcessEnv(): EnvironmentName | undefined {
    if (hasProcessAccess() && process.env.REACT_APP_ENVIRONMENT_NAME) {
        return process.env.REACT_APP_ENVIRONMENT_NAME as EnvironmentName;
    }

    // Jest "doesn't count" as node - the logic in this function is mostly for use with e.g. backend
    if (!isNode() || areWeTestingWithJest()) {
        return undefined;
    }

    return process.env.FUNCTIONS_EMULATOR ? EnvironmentName.Local : EnvironmentName.Production;
}

export function createMergeRequestSlug(): string | undefined {
    const mergeRequestId = getMergeRequestId();
    if (mergeRequestId) {
        const environmentNamePrefix = "MR-";
        return environmentNamePrefix + mergeRequestId;
    }
    return undefined;
}

/**
 * Whether we are running on a PR preview environment (pr-N.preview.flux.ai).
 * Derived at runtime from the proxy stamp or the hostname.
 * NOTE: This is true for all UI tests.
 */
export function isMergeRequestPreviewEnvironment(): boolean {
    return getMergeRequestId() !== undefined;
}

/**
 * We attach environment information to the window so we can check it at runtime in different environments
 */
declare global {
    interface Window {
        __FLUX__: {
            env: {
                isDevEnv: boolean;
                environmentName: EnvironmentName;
            };
        };
    }
}
// TODO: Move debug registration to explicit application startup after checking browser QA consumers.
if (hasBrowserWindowAccess()) {
    set(window, "__FLUX__.env.isDevEnv", isDevEnv());
    set(window, "__FLUX__.env.environmentName", getEnvironmentName());
    set(window, "__FLUX__.env.hasProcessAccess", hasProcessAccess());
    set(window, "__FLUX__.env.shouldUseLocalServices.all", shouldUseLocalServices());
    set(window, "__FLUX__.env.shouldUseLocalServices.foo", shouldUseLocalServices("foo"));
}
